import React, { useCallback, useEffect, useState, useRef } from "react"
import { api } from "../api/client"
import { useLanguage } from "../context/LanguageContext"
import { SkeletonCard, SkeletonText, SkeletonButton } from "../components/LoadingSpinner"
import ListeningScene from "../components/ListeningScene"
import ExerciseVocabulary from "../components/ExerciseVocabulary"
import { scrollToTop, scrollResultIntoView } from "../utils/scroll"

const CARD_WAVE = [0.35, 0.7, 0.45, 0.9, 0.55, 0.8, 0.4]
const SPEED_OPTIONS = [0.75, 1, 1.25]
const SKIP_SECONDS = 5

function translateDifficulty(level, t) {
  switch (level) {
    case "Beginner":
      return t("beginner")
    case "Intermediate":
      return t("intermediate")
    case "Advanced":
      return t("advanced")
    default:
      return level
  }
}

function difficultyChipClass(level) {
  return `listening-chip listening-chip--${(level || "default").toLowerCase()}`
}

function formatClock(seconds) {
  const total = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`
}

export default function Listening() {
  const { t } = useLanguage()
  const [view, setView] = useState("picker")
  const [lessons, setLessons] = useState([])
  const [lessonsLoading, setLessonsLoading] = useState(true)
  const [difficulty, setDifficulty] = useState("all")
  const [hoveredLessonId, setHoveredLessonId] = useState(null)
  const [exercise, setExercise] = useState(null)
  const [answers, setAnswers] = useState({})
  const [score, setScore] = useState(null)
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [showTranscript, setShowTranscript] = useState(false)
  const [selectedText, setSelectedText] = useState("")
  const [useTTS, setUseTTS] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [playbackRate, setPlaybackRate] = useState(1)
  const [playCount, setPlayCount] = useState(0)
  const audioRef = useRef(null)
  const ttsRef = useRef(null)
  const shapesRef = useRef(null)
  const sessionStartedAtRef = useRef(Date.now())
  const lessonsLoadedRef = useRef(false)

  useEffect(() => {
    if (!exercise) return
    setIsPlaying(false)
    setUseTTS(false)
    setCurrentTime(0)
    setDuration(0)
    setPlayCount(0)
    setPlaybackRate(1)
    sessionStartedAtRef.current = Date.now()
    if (audioRef.current && exercise.audio_url) {
      try {
        audioRef.current.src = exercise.audio_url
        audioRef.current.playbackRate = 1
        audioRef.current.load()
      } catch {
        setUseTTS(true)
      }
    }
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel()
    }
  }, [exercise])

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = playbackRate
  }, [playbackRate])

  useEffect(() => {
    const mq = window.matchMedia("(pointer: fine)")
    const onMove = (e) => {
      if (!mq.matches) return
      const layer = shapesRef.current
      if (!layer) return
      const x = (e.clientX / window.innerWidth - 0.5) * 2
      const y = (e.clientY / window.innerHeight - 0.5) * 2
      layer.style.transform = `rotateX(${y * -3}deg) rotateY(${x * 5}deg)`
    }
    window.addEventListener("mousemove", onMove)
    return () => window.removeEventListener("mousemove", onMove)
  }, [])

  const loadLessons = async (selectedDifficulty = difficulty) => {
    if (!lessonsLoadedRef.current) setLessonsLoading(true)
    setError("")
    try {
      const data = await api.listListeningExercises(
        selectedDifficulty !== "all" ? selectedDifficulty : null
      )
      setLessons(data.exercises || [])
      lessonsLoadedRef.current = true
    } catch (e) {
      setError(e.message)
      setLessons([])
    } finally {
      setLessonsLoading(false)
    }
  }

  const loadExercise = async ({ exerciseId, title, selectedDifficulty = difficulty } = {}) => {
    scrollToTop()
    setLoading(true)
    setError("")
    setScore(null)
    setAnswers({})
    setShowTranscript(false)
    setIsPlaying(false)
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel()
    }
    try {
      const ex = await api.listeningExercise({
        difficulty: selectedDifficulty !== "all" ? selectedDifficulty : null,
        exerciseId: exerciseId || null,
        title: title || null,
      })
      setExercise(ex)
      setView("practice")
      scrollToTop()
    } catch (e) {
      setError(e.message)
      setView("picker")
    } finally {
      setLoading(false)
    }
  }

  const backToLessons = () => {
    setView("picker")
    setExercise(null)
    setScore(null)
    setAnswers({})
    setIsPlaying(false)
    setShowTranscript(false)
    setError("")
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel()
    }
    scrollToTop()
  }

  useEffect(() => {
    loadLessons()
  }, [])

  const usingTTS = useTTS || !exercise?.audio_url

  const speak = useCallback(() => {
    if (!("speechSynthesis" in window)) {
      setError(t("listeningAudioUnavailable"))
      return
    }
    const utterance = new SpeechSynthesisUtterance(exercise?.transcript || "")
    utterance.rate = 0.9 * playbackRate
    utterance.onend = () => setIsPlaying(false)
    utterance.onerror = () => setIsPlaying(false)
    ttsRef.current = utterance
    window.speechSynthesis.speak(utterance)
    setIsPlaying(true)
    setPlayCount((n) => n + 1)
  }, [exercise?.transcript, playbackRate, t])

  const toggleAudio = useCallback(() => {
    if (usingTTS) {
      if (isPlaying) {
        window.speechSynthesis?.cancel()
        setIsPlaying(false)
        return
      }
      speak()
      return
    }
    const audio = audioRef.current
    if (!audio) return
    if (isPlaying) {
      audio.pause()
      setIsPlaying(false)
      return
    }
    audio.playbackRate = playbackRate
    audio.play()
    setIsPlaying(true)
    setPlayCount((n) => n + 1)
  }, [usingTTS, isPlaying, speak, playbackRate])

  const replayFromStart = useCallback(() => {
    if (usingTTS) {
      window.speechSynthesis?.cancel()
      speak()
      return
    }
    const audio = audioRef.current
    if (!audio) return
    audio.currentTime = 0
    setCurrentTime(0)
    audio.playbackRate = playbackRate
    audio.play()
    setIsPlaying(true)
    setPlayCount((n) => n + 1)
  }, [usingTTS, speak, playbackRate])

  const skip = useCallback(
    (delta) => {
      const audio = audioRef.current
      if (usingTTS || !audio) return
      const limit = Number.isFinite(audio.duration) ? audio.duration : 0
      const next = Math.min(Math.max(audio.currentTime + delta, 0), limit || audio.currentTime)
      audio.currentTime = next
      setCurrentTime(next)
    },
    [usingTTS]
  )

  const seekTo = (value) => {
    const audio = audioRef.current
    if (!audio) return
    audio.currentTime = value
    setCurrentTime(value)
  }

  useEffect(() => {
    if (view !== "practice") return
    const onKey = (e) => {
      const el = e.target
      const tag = el?.tagName
      if (
        el?.isContentEditable ||
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        tag === "BUTTON" ||
        tag === "A"
      ) {
        return
      }
      if (e.code === "Space") {
        e.preventDefault()
        toggleAudio()
      } else if (e.code === "ArrowLeft") {
        e.preventDefault()
        skip(-SKIP_SECONDS)
      } else if (e.code === "ArrowRight") {
        e.preventDefault()
        skip(SKIP_SECONDS)
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [view, toggleAudio, skip])

  const submit = async () => {
    if (!exercise) return
    setSubmitting(true)
    try {
      const payload = {
        lesson_id: exercise.lesson_id,
        time_spent: Math.max(
          1,
          Math.round((Date.now() - sessionStartedAtRef.current) / 1000)
        ),
        answers: Object.entries(answers).map(([id, answer]) => ({ id: Number(id), answer })),
      }
      const res = await api.submitListening(payload)
      setScore(res.score)
      scrollResultIntoView()
    } catch (e) {
      setError(e.message)
    } finally {
      setSubmitting(false)
    }
  }

  const levelOptions = [
    { key: "all", label: t("allLevels") },
    { key: "Beginner", label: t("beginner") },
    { key: "Intermediate", label: t("intermediate") },
    { key: "Advanced", label: t("advanced") },
  ]

  const sceneShell = (children) => (
    <div className="min-h-screen listening-page-bg relative overflow-hidden">
      <ListeningScene layerRef={shapesRef} />
      <div key={view} className="relative z-10 listening-scene view-stage">
        {children}
      </div>
    </div>
  )

  if (lessonsLoading || (view === "practice" && loading)) {
    return sceneShell(
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        <div className="animate-pulse space-y-8">
          <div className="text-center space-y-4 pt-8">
            <SkeletonText lines={2} className="max-w-md mx-auto" />
          </div>
          <div className="flex justify-center gap-3 flex-wrap">
            <SkeletonButton className="w-24" />
            <SkeletonButton className="w-24" />
            <SkeletonButton className="w-24" />
          </div>
          <div className="flex flex-col gap-3">
            <SkeletonCard className="h-28 rounded-2xl" />
            <SkeletonCard className="h-28 rounded-2xl" />
            <SkeletonCard className="h-28 rounded-2xl" />
          </div>
        </div>
      </div>
    )
  }

  if (error && view === "picker" && lessons.length === 0) {
    return sceneShell(
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="listening-glass p-6 sm:p-8 max-w-md w-full text-center">
          <h3 className="text-base font-semibold text-foreground mb-2">{t("somethingWentWrong")}</h3>
          <p className="text-destructive mb-4 text-sm">{error}</p>
          <button
            type="button"
            onClick={() => loadLessons()}
            className="button-glow bg-gradient-to-r from-primary to-accent text-primary-foreground px-6 py-2 rounded-xl hover:opacity-95 transition-all text-sm font-medium"
          >
            {t("tryAgain")}
          </button>
        </div>
      </div>
    )
  }

  if (view === "picker") {
    return sceneShell(
      <div className="listening-picker max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 w-full min-w-0 py-6 md:py-10">
        <header className="listening-hero-minimal listening-picker-hero mb-6 md:mb-8">
          <p className="listening-picker-eyebrow">{t("listeningClassroom")}</p>
          <h1 className="listening-hero-title">{t("listening")}</h1>
          <div className="listening-hero-accent" aria-hidden />
          <p className="listening-picker-lead">{t("listeningPickShelf")}</p>
        </header>

        <section className="listening-band-dock mb-6 sm:mb-8">
          <p className="listening-band-dock-label">{t("listeningChooseLevel")}</p>
          <div className="listening-band-dock-track" role="tablist" aria-label={t("listeningChooseLevel")}>
            {levelOptions.map((level) => {
              const isActive = difficulty === level.key
              return (
                <button
                  key={level.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => {
                    setDifficulty(level.key)
                    loadLessons(level.key)
                  }}
                  className={`listening-band-tab ${isActive ? "listening-band-tab--active" : ""}`}
                >
                  {level.label}
                </button>
              )
            })}
          </div>
        </section>

        <section className="w-full min-w-0">
          <div className="listening-shelf-header">
            <h2 className="listening-shelf-title">{t("listeningChooseLesson")}</h2>
            <p className="listening-shelf-count">
              {lessons.length}{" "}
              {lessons.length === 1 ? t("listeningLesson") : t("listeningLessons")}
            </p>
          </div>

          {error && <p className="text-sm text-destructive mb-4">{error}</p>}

          <div className="listening-library-grid">
            {lessons.map((lesson) => {
              const lessonKey = lesson.id || lesson.title
              const isHovered = hoveredLessonId === lessonKey
              return (
                <button
                  key={lessonKey}
                  type="button"
                  onMouseEnter={() => setHoveredLessonId(lessonKey)}
                  onMouseLeave={() => setHoveredLessonId(null)}
                  onFocus={() => setHoveredLessonId(lessonKey)}
                  onBlur={() => setHoveredLessonId(null)}
                  onClick={() =>
                    loadExercise({
                      exerciseId: lesson.id,
                      title: lesson.title,
                      selectedDifficulty: difficulty,
                    })
                  }
                  className={`listening-lesson-card${isHovered ? " is-live" : ""}`}
                >
                  <span className="listening-lesson-rail" aria-hidden />
                  <div className="listening-lesson-body">
                    <div className="listening-lesson-kicker">
                      <span className={difficultyChipClass(lesson.difficulty)}>
                        {translateDifficulty(lesson.difficulty, t)}
                      </span>
                      <span className={`listening-chip ${lesson.has_audio ? "listening-chip--live" : "listening-chip--ghost"}`}>
                        {lesson.has_audio ? t("listeningHasAudio") : t("listeningVoiceOnly")}
                      </span>
                    </div>
                    <h3 className="listening-lesson-title">{lesson.title}</h3>
                    <div className="listening-lesson-stats">
                      <span>~{lesson.estimated_duration} {t("listeningMin")}</span>
                      <span>
                        {lesson.questions_count} {t("listeningQuestionsCount")}
                      </span>
                    </div>
                    <div
                      className={`listening-track-spectrum ${isHovered ? "listening-track-spectrum--live" : ""}`}
                      aria-hidden
                    >
                      {CARD_WAVE.map((h, i) => (
                        <span
                          key={i}
                          className="listening-track-spectrum-bar"
                          style={{
                            height: `${28 + h * 72}%`,
                            animationDelay: `${i * 0.08}s`,
                          }}
                        />
                      ))}
                    </div>
                    {lesson.instructions && (
                      <p className="listening-lesson-blurb">{lesson.instructions}</p>
                    )}
                    <span className="listening-lesson-cta">
                      {t("listeningStartLesson")}
                      <span className="listening-lesson-cta-icon" aria-hidden>→</span>
                    </span>
                  </div>
                </button>
              )
            })}
          </div>

          {lessons.length === 0 && !error && (
            <div className="listening-library-empty">
              {t("listeningNoLessons")}
            </div>
          )}
        </section>
      </div>
    )
  }

  if (!exercise) return null

  const questions = Array.isArray(exercise.questions) ? exercise.questions : []
  const totalQuestions = questions.length
  const answeredQuestions = Object.keys(answers).length
  const allAnswered = answeredQuestions === totalQuestions && totalQuestions > 0
  const canSeek = !usingTTS && duration > 0
  const seekPercentage = canSeek ? Math.min((currentTime / duration) * 100, 100) : 0

  const getScoreColor = (s) => {
    if (s >= 80) return "text-primary"
    if (s >= 60) return "text-amber-600 dark:text-amber-400"
    return "text-destructive"
  }

  const getScoreBgColor = (s) => {
    if (s >= 80) return "bg-primary/10 border-primary/30"
    if (s >= 60) return "bg-amber-500/10 border-amber-500/30"
    return "bg-destructive/10 border-destructive/30"
  }

  return sceneShell(
    <div className="listening-practice">
      <button type="button" onClick={backToLessons} className="listening-back">
        ← {t("listeningBackToLessons")}
      </button>

      <article className="listening-lab">
        <header className="listening-head">
          <div className="listening-head-meta">
            <span className={difficultyChipClass(exercise.difficulty)}>
              {translateDifficulty(exercise.difficulty, t)}
            </span>
            <span className="listening-head-count tabular-nums">
              {answeredQuestions}/{totalQuestions} {t("listeningAnswered")}
            </span>
          </div>
          <h1 className="listening-head-title">{exercise.title}</h1>
          <p className="listening-head-lead">
            {exercise.instructions || t("listenToTheAudioAndAnswerQuestions")}
          </p>
          {usingTTS && <p className="listening-tts-note">{t("listeningTtsFallback")}</p>}
        </header>

        <div className="listening-audio">
          <div className="listening-transport">
            <button
              type="button"
              onClick={toggleAudio}
              className={`listening-play-btn${isPlaying ? " is-playing" : ""}`}
              aria-pressed={isPlaying}
              aria-label={isPlaying ? t("pauseAudio") : t("playAudio")}
            >
              <span aria-hidden>{isPlaying ? "❚❚" : "▶"}</span>
            </button>
            <div className="listening-seek">
              {canSeek ? (
                <input
                  type="range"
                  className="listening-seek-range"
                  min={0}
                  max={duration}
                  step={0.1}
                  value={Math.min(currentTime, duration)}
                  onChange={(e) => seekTo(Number(e.target.value))}
                  aria-label={t("listeningSeek")}
                  style={{ "--seek-pct": `${seekPercentage}%` }}
                />
              ) : (
                <p className="listening-stage-state">
                  {isPlaying ? t("listeningNowPlaying") : t("listeningPressPlay")}
                </p>
              )}
              <div className="listening-seek-times tabular-nums">
                <span>{formatClock(currentTime)}</span>
                {playCount > 0 && (
                  <span>{t("listeningPlayedTimes").replace("{n}", String(playCount))}</span>
                )}
                <span>{canSeek ? formatClock(duration) : "—"}</span>
              </div>
            </div>
          </div>

          <div className="listening-controls">
            <button
              type="button"
              onClick={() => skip(-SKIP_SECONDS)}
              className="listening-ctl"
              disabled={!canSeek}
              aria-label={t("listeningBackSeconds").replace("{n}", String(SKIP_SECONDS))}
            >
              −{SKIP_SECONDS}s
            </button>
            <button
              type="button"
              onClick={replayFromStart}
              className="listening-ctl"
              aria-label={t("listeningRestart")}
            >
              {t("listeningReplay")}
            </button>
            <button
              type="button"
              onClick={() => skip(SKIP_SECONDS)}
              className="listening-ctl"
              disabled={!canSeek}
              aria-label={t("listeningForwardSeconds").replace("{n}", String(SKIP_SECONDS))}
            >
              +{SKIP_SECONDS}s
            </button>
            <div className="listening-speed" role="group" aria-label={t("listeningSpeed")}>
              {SPEED_OPTIONS.map((rate) => (
                <button
                  key={rate}
                  type="button"
                  onClick={() => setPlaybackRate(rate)}
                  aria-pressed={playbackRate === rate}
                  className={`listening-speed-btn${playbackRate === rate ? " is-on" : ""}`}
                >
                  {rate}×
                </button>
              ))}
            </div>
          </div>

          {exercise.transcript && (
            <button
              type="button"
              onClick={() => setShowTranscript(!showTranscript)}
              className="listening-transcript-toggle"
              aria-expanded={showTranscript}
            >
              {showTranscript ? t("hideTranscript") : t("showTranscript")}
            </button>
          )}

          <audio
            ref={audioRef}
            onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
            onDurationChange={(e) => setDuration(e.currentTarget.duration || 0)}
            onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime || 0)}
            onEnded={() => setIsPlaying(false)}
            onError={() => {
              setUseTTS(true)
              setIsPlaying(false)
            }}
            className="sr-only"
          >
            {exercise.audio_url && <source src={exercise.audio_url} type="audio/mpeg" />}
            {t("audioNotSupported")}
          </audio>

          {showTranscript && exercise.transcript && (
            <div className="listening-transcript">
              <p
                className="select-text"
                onMouseUp={() => {
                  const text = window.getSelection()?.toString().trim()
                  if (text) setSelectedText(text)
                }}
              >
                {exercise.transcript}
              </p>
              <ExerciseVocabulary
                skill="listening"
                lessonId={exercise.lesson_id}
                lessonTitle={exercise.title}
                sourceRef={exercise.id}
                selectedText={selectedText}
                onClearSelection={() => setSelectedText("")}
                contextText={exercise.transcript}
              />
            </div>
          )}
        </div>

        <div className="listening-worksheet">
          <div className="listening-q-list">
            {questions.map((q, index) => (
              <div
                key={q.id}
                className={`listening-q${answers[q.id] !== undefined ? " is-answered" : ""}`}
              >
                <span className="listening-q-num">{index + 1}</span>
                <div className="listening-q-body">
                  <p className="listening-q-text">{q.question || q.q}</p>
                  {Array.isArray(q.options) && q.options.length > 0 && (
                    <div className="listening-mcq-options">
                      {q.options.map((option, optIndex) => (
                        <label
                          key={optIndex}
                          className={`listening-mcq-option${answers[q.id] === option ? " is-on" : ""}`}
                        >
                          <input
                            type="radio"
                            name={`q_${q.id}`}
                            value={option}
                            checked={answers[q.id] === option}
                            onChange={(e) =>
                              setAnswers({ ...answers, [q.id]: e.target.value })
                            }
                          />
                          <span>{option}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {error && exercise && (
            <p className="mt-4 text-sm text-destructive text-center">{error}</p>
          )}

          <div className="listening-actions">
            <button
              type="button"
              onClick={submit}
              disabled={!allAnswered || submitting}
              className="listening-btn listening-btn--primary listening-btn--wide"
            >
              {submitting ? t("submitting") : t("submitAnswers")}
            </button>
          </div>

          {score !== null && (
            <div className={`listening-score ui-reveal ${getScoreBgColor(score)}`}>
              <h3>{t("greatWork")}</h3>
              <p className={`listening-score-num ${getScoreColor(score)}`}>{score}%</p>
              <p>{t("keepPracticingListening")}</p>
            </div>
          )}
        </div>
      </article>
    </div>
  )
}
